import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import s3awsConfig from 'utils/aws-config';
import { PutObjectCommand } from '@aws-sdk/client-s3';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { getInstructionsForPose } from 'utils/poseInstructions';
import { createAsana } from 'services/asana/createAsana';
import VideoTrimmerModal from './VideoTrimmerModal';

function PoseCaptureHandler({
  poseLandmarkerRef,
  webcamRef,
  posture,
  setPoseCaptureComplete,
  setCheckingVisibility,
  setAttempts,
  isPoseLandmarkerReady,
  isFullBodyVisible,
  setIsRecording,
  setRecordingDuration,
  onStopRecordingRef,
  maxRecordingSeconds = 20,
  captureIntervalMs = 500,
}) {
  const queryClient = useQueryClient();

  const coachId = localStorage.getItem('userid');
  const location = useLocation();
  const navigate = useNavigate();
  const source = location.state?.source || localStorage.getItem('navigationSource') || 'instructor';

  // Refs
  const isRecordingRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const captureIntervalRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const instructionIntervalRef = useRef(null);
  const isMutationTriggered = useRef(false);
  const initialLandmarksRef = useRef(null);
  
  const [videoBlob, setVideoBlob] = useState(null);

  // Mutation
  const { mutateAsync: createMutation } = useMutation({
    mutationFn: createAsana,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asanas'] });
      queryClient.invalidateQueries({ queryKey: ['manager-allcourses'] });

      if (data?.status) {
        toast.success('Pose video saved successfully! 🎉');
        setPoseCaptureComplete(true);

        setTimeout(() => {
          navigate(source === 'library' ? '/library' : '/instructor', { replace: true });
        }, 1500);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Something went wrong');
      setCheckingVisibility(true);
      setAttempts((prev) => prev + 1);
    },
  });

  // Upload to S3 + Save to DB
  const uploadAndSave = async (blob, selectedFrameLandmarks, imageBlob) => {
    try {
      // The reference pose must come from the frame the coach picked. Falling
      // back to the first detected frame would save whatever they happened to
      // be doing when recording started.
      const landmarks = selectedFrameLandmarks?.length
        ? selectedFrameLandmarks
        : initialLandmarksRef.current;

      if (!landmarks?.length) {
        toast.error('No reference pose was captured. Please retake the pose.');
        setCheckingVisibility(true);
        setAttempts((prev) => prev + 1);
        return;
      }

      toast.loading('Uploading pose video…', { id: 'upload-toast' });

      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const fileKey = `Uploads/${uuidv4()}.webm`;

      await s3awsConfig.send(
        new PutObjectCommand({
          Bucket: 'roacs-bucket',
          Key: fileKey,
          Body: uint8Array,
          ContentType: 'video/webm',
        })
      );

      const videoUrl = `https://roacs-bucket.s3.ap-south-1.amazonaws.com/${fileKey}`;

      let imageUrl = null;
      if (imageBlob) {
        const imageArrayBuffer = await imageBlob.arrayBuffer();
        const imageUint8Array = new Uint8Array(imageArrayBuffer);
        const imageFileKey = `Uploads/${uuidv4()}.jpg`;

        await s3awsConfig.send(
          new PutObjectCommand({
            Bucket: 'roacs-bucket',
            Key: imageFileKey,
            Body: imageUint8Array,
            ContentType: 'image/jpeg',
          })
        );
        imageUrl = `https://roacs-bucket.s3.ap-south-1.amazonaws.com/${imageFileKey}`;
      }

      toast.dismiss('upload-toast');

      await createMutation({
        coachId: Number(coachId),
        courseId: posture.courseId,
        posturename: posture.posturename,
        posturenumber: posture.posturenumber,
        landmarks,
        videoUrl: videoUrl,
        video: videoUrl,
        image: imageUrl,
        asanaId: posture.postureId || Number(localStorage.getItem('asanaId')), // Prevents duplicates
      });
    } catch (err) {
      toast.dismiss('upload-toast');
      console.error('Upload error:', err);
      toast.error('Failed to save pose');
      setCheckingVisibility(true);
      setAttempts((prev) => prev + 1);
      isMutationTriggered.current = false;
    }
  };

  // Speak next instruction
  const speakNextInstruction = () => {
    if (!posture?.posturename) return;
    const instructions = getInstructionsForPose(posture.posturename);
    const text = instructions[Math.floor(Math.random() * instructions.length)];

    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.05;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Start Clean Recording
  const startRecording = async () => {
    if (isRecordingRef.current || isMutationTriggered.current) return;
    isMutationTriggered.current = true;

    try {
      const video = webcamRef.current?.video;
      if (!video || video.readyState < 3) throw new Error('Webcam not ready');

      // Canvas for clean mirrored recording
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');

      const fps = Math.round(1000 / captureIntervalMs);
      const canvasStream = canvas.captureStream(fps);

      chunksRef.current = [];
      initialLandmarksRef.current = null;

      const recorder = new MediaRecorder(canvasStream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : 'video/webm',
      });

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setVideoBlob(blob);
        setCheckingVisibility(false);
      };

      recorder.start(500);
      isRecordingRef.current = true;
      setIsRecording(true);

      // Mirror only (Clean Video - No Skeleton)
      captureIntervalRef.current = setInterval(() => {
        if (!video || video.readyState < 3) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
        ctx.restore();

        // Capture initial landmarks
        if (!initialLandmarksRef.current && poseLandmarkerRef.current) {
          const results = poseLandmarkerRef.current.detectForVideo(video, performance.now());
          if (results?.landmarks?.[0]) {
            initialLandmarksRef.current = results.landmarks[0].map((lm) => ({
              x: 1 - lm.x,
              y: lm.y,
              z: lm.z || 0,
              visibility: lm.visibility || 0,
            }));
          }
        }
      }, captureIntervalMs);

      // Duration counter
      let elapsed = 0;
      setRecordingDuration(maxRecordingSeconds);
      durationIntervalRef.current = setInterval(() => {
        elapsed += 1;
        setRecordingDuration(Math.max(0, maxRecordingSeconds - elapsed));
      }, 1000);

      // Auto-stop
      autoStopTimerRef.current = setTimeout(stopRecording, maxRecordingSeconds * 1000);

      // Automatic Voice Instructions
      speakNextInstruction(); // First instruction immediately
      instructionIntervalRef.current = setInterval(() => {
        if (isRecordingRef.current) speakNextInstruction();
      }, 5500);

      toast.success(`🔴 Recording started - Demonstrate full asana`, { id: 'rec-start' });

    } catch (err) {
      console.error('Start recording error:', err);
      toast.error(err.message || 'Failed to start recording');
      isMutationTriggered.current = false;
    }
  };

  const stopRecording = () => {
    if (!isRecordingRef.current) return;

    isRecordingRef.current = false;
    clearInterval(captureIntervalRef.current);
    clearInterval(instructionIntervalRef.current);
    clearTimeout(autoStopTimerRef.current);
    clearInterval(durationIntervalRef.current);

    setIsRecording(false);
    setRecordingDuration(0);

    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    window.speechSynthesis?.cancel();
  };

  // Auto-start when full body detected
  useEffect(() => {
    if (isFullBodyVisible && !isRecordingRef.current && !isMutationTriggered.current) {
      startRecording();
    }
  }, [isFullBodyVisible]);

  // Expose stop function
  useEffect(() => {
    if (onStopRecordingRef) {
      onStopRecordingRef.current = stopRecording;
    }
  }, [onStopRecordingRef]);

  // Cleanup
  useEffect(() => {
    return () => {
      clearInterval(captureIntervalRef.current);
      clearInterval(instructionIntervalRef.current);
      clearTimeout(autoStopTimerRef.current);
      clearInterval(durationIntervalRef.current);
      window.speechSynthesis?.cancel();
    };
  }, []);

  return (
    <>
      {videoBlob && (
        <VideoTrimmerModal
          videoBlob={videoBlob}
          onSave={async (trimmedBlob, selectedFrameLandmarks, imageBlob) => {
            await uploadAndSave(trimmedBlob, selectedFrameLandmarks, imageBlob);
            setVideoBlob(null);
          }}
          onCancel={() => {
            setVideoBlob(null);
            setCheckingVisibility(true);
            isMutationTriggered.current = false;
          }}
        />
      )}
    </>
  );
}

export default PoseCaptureHandler;